import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * DatePicker — shadcn Popover + Calendar.
 * Stores value as ISO date string "YYYY-MM-DD" (compatible with existing backend fields).
 *
 * Props:
 *   value: "YYYY-MM-DD" or ""
 *   onChange: (str) => void
 *   placeholder?: string
 *   testId?: string
 *   clearable?: boolean (default true)
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  testId = "datepicker",
  clearable = true,
}) {
  const [open, setOpen] = useState(false);

  const date =
    value && typeof value === "string"
      ? (() => {
          const d = parse(value, "yyyy-MM-dd", new Date());
          return isValid(d) ? d : undefined;
        })()
      : undefined;

  const handleSelect = (d) => {
    if (!d) {
      onChange("");
    } else {
      onChange(format(d, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-input bg-white text-sm h-10 transition-colors hover:border-[var(--rsi-purple)] focus:outline-none focus:ring-2 focus:ring-[var(--rsi-purple)]/30 focus:border-[var(--rsi-purple)] ${
            date ? "text-[var(--rsi-navy)]" : "text-gray-400"
          }`}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarIcon className="h-4 w-4 text-gray-400 shrink-0" />
            <span className="font-mono-plex">{date ? format(date, "MMM d, yyyy") : placeholder}</span>
          </span>
          {clearable && date && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clear(e);
                }
              }}
              data-testid={`${testId}-clear`}
              className="h-5 w-5 rounded-full grid place-items-center text-gray-400 hover:text-red-600 hover:bg-gray-100 cursor-pointer"
              aria-label="Clear date"
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <Calendar mode="single" selected={date} onSelect={handleSelect} initialFocus />
      </PopoverContent>
    </Popover>
  );
}
